// Report flow item aktivite akisi (sistem uretir, kullanici yazamaz).
// CommentSection'a paralel gorsel dil — sistem badge'i + zaman + kim.
// Trigger otomatik doldurur: kutucuk tasindiginda/arsivlendiginde row eklenir.

import React from 'react';
import {
  Box, Typography, CircularProgress, Avatar, Chip, useTheme, alpha,
} from '@mui/material';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import ArchiveIcon from '@mui/icons-material/Archive';
import UnarchiveIcon from '@mui/icons-material/Unarchive';
import HistoryIcon from '@mui/icons-material/History';
import { useTranslation } from 'react-i18next';
import { useReportFlowItemActivities } from '../../hooks/useReportFlowItemActivities';

const stringToColor = (str = '') => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) hash = str.charCodeAt(i) + ((hash << 5) - hash);
  const h = Math.abs(hash) % 360;
  return `hsl(${h}, 45%, 55%)`;
};

const formatDT = (v) => {
  if (!v) return '—';
  try {
    return new Date(v).toLocaleString('tr-TR', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return v; }
};

const ACTIVITY_META = {
  column_moved: { icon: SwapHorizIcon,  color: '#3b82f6' },
  archived:     { icon: ArchiveIcon,    color: '#94a3b8' },
  unarchived:   { icon: UnarchiveIcon,  color: '#10b981' },
};

const describeActivity = (a, columnNames, t) => {
  // from/to_value TEXT olarak saklaniyor; column lookup integer key ile
  // dolduruluyor. Iki tarafi da normalize et.
  const fromKey = parseInt(a.from_value, 10);
  const toKey   = parseInt(a.to_value, 10);
  const from = columnNames[fromKey] || a.from_value || '—';
  const to   = columnNames[toKey]   || a.to_value   || '—';
  switch (a.activity_type) {
    case 'column_moved':
      return t('activity.moved', {
        defaultValue: `{{from}} → {{to}}`,
        from, to,
      });
    case 'archived':
      return t('activity.archived', { defaultValue: 'Kutucuk arşivlendi' });
    case 'unarchived':
      return t('activity.unarchived', { defaultValue: 'Arşivden çıkarıldı' });
    default:
      return a.activity_type;
  }
};

const ReportFlowItemActivityFeed = ({ itemId, sx }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const { activities, columnNames, loading } = useReportFlowItemActivities(itemId);

  return (
    <Box sx={{
      display: 'flex', flexDirection: 'column',
      borderRadius: 2, overflow: 'hidden',
      border: `1px solid ${theme.palette.divider}`,
      bgcolor: isDark ? alpha(theme.palette.common.black, 0.2) : alpha(theme.palette.grey[50], 0.8),
      ...sx,
    }}>
      <Box sx={{
        px: 2, py: 1, display: 'flex', alignItems: 'center', gap: 1,
        borderBottom: `1px solid ${theme.palette.divider}`,
      }}>
        <HistoryIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
        <Typography sx={{
          fontSize: 11, fontWeight: 700, letterSpacing: '0.09em',
          textTransform: 'uppercase', color: 'text.disabled',
        }}>
          {t('activity.title', { defaultValue: 'Aktivite Geçmişi' })}
        </Typography>
        {activities.length > 0 && (
          <Chip label={activities.length} size="small" sx={{
            height: 18, fontSize: 10.5, fontWeight: 700,
            bgcolor: 'action.hover', ml: 'auto',
          }} />
        )}
      </Box>

      <Box sx={{
        flex: 1, overflowY: 'auto', px: 2, py: 1.5,
        maxHeight: 240,
      }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={20} thickness={3} />
          </Box>
        ) : activities.length === 0 ? (
          <Typography sx={{
            fontSize: 12.5, color: 'text.disabled', fontStyle: 'italic',
            textAlign: 'center', py: 2,
          }}>
            {t('activity.empty', { defaultValue: 'Henüz aktivite yok' })}
          </Typography>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
            {activities.map((a) => {
              const meta = ACTIVITY_META[a.activity_type] || {};
              const Icon = meta.icon || HistoryIcon;
              const color = meta.color || '#94a3b8';
              const actorName = a.actor
                ? `${a.actor.first_name || ''} ${a.actor.last_name || ''}`.trim() || `#${a.actor.id}`
                : t('activity.system', { defaultValue: 'Sistem' });
              return (
                <Box key={a.id} sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-start' }}>
                  <Avatar sx={{
                    width: 26, height: 26, fontSize: 11, fontWeight: 700,
                    bgcolor: a.actor ? stringToColor(actorName) : alpha(color, 0.15),
                    color: a.actor ? '#fff' : color,
                  }}>
                    {a.actor
                      ? actorName.split(/\s+/).map(s => s[0]).slice(0, 2).join('').toUpperCase()
                      : <Icon sx={{ fontSize: 15 }} />}
                  </Avatar>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                      <Typography component="span" sx={{ fontSize: 12.5, fontWeight: 700 }}>
                        {actorName}
                      </Typography>
                      <Chip
                        icon={<Icon sx={{ fontSize: 11, color: `${color} !important` }} />}
                        label={describeActivity(a, columnNames, t)}
                        size="small"
                        sx={{
                          height: 20, fontSize: 11, fontWeight: 600,
                          bgcolor: alpha(color, 0.10), color,
                          '& .MuiChip-icon': { ml: 0.75 },
                        }}
                      />
                    </Box>
                    <Typography sx={{ fontSize: 10.5, color: 'text.disabled', mt: 0.15 }}>
                      {formatDT(a.occurred_at)}
                    </Typography>
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default ReportFlowItemActivityFeed;
